import { ResourcesService } from '@mitsubishi/core';
import { ProjectStatisticsCounts } from '@mitsubishi/project/data-access';

export const INACTIVE_ICON_COLOR = 'var(--apm-light-gray2)';

const STATUS_RESOURCE_IDS = {
  quoteDraft: 448759,
  quoteExpired: 448760,
  offerDraft: 448753,
  offerFinal: 448755,
  offerExpired: 448754,
  offerArchived: 448752,
  offerLost: 448756,
  orderError: 448758,
  orderDraft: 448757,
  orderPending: 465159,
  orderFinal: 448761,
} as const;

const QUOTE_TOOLTIP_FALLBACK_RESOURCE_ID = 444923;
const OFFER_TOOLTIP_FALLBACK_RESOURCE_ID = 444924;
const ORDER_TOOLTIP_FALLBACK_RESOURCE_ID = 444925;

function buildTooltip(
  states: { count: number; resourceId: number }[],
  fallbackResourceId: number,
  resourcesService: ResourcesService
): string {
  const tooltip = states
    .filter(state => state.count > 0)
    .map(state => `${state.count} ${resourcesService.getTextResource(state.resourceId)}`)
    .join('\n');

  return tooltip || resourcesService.getTextResource(fallbackResourceId);
}

/**
 * I totali mostrati accanto alle icone sono la somma esatta degli stati elencati dal rispettivo
 * tooltip: badge e breakdown non devono mai divergere.
 */
export function getQuoteTotal(counts: ProjectStatisticsCounts): number {
  return counts.quoteDraftCount + counts.quoteExpiredCount || 0;
}

export function getOfferTotal(counts: ProjectStatisticsCounts): number {
  return (
    counts.offerDraftCount +
      counts.offerFinalCount +
      counts.offerExpiredCount +
      counts.offerArchivedCount +
      counts.offerLostCount || 0
  );
}

export function getOrderTotal(counts: ProjectStatisticsCounts): number {
  return counts.orderDraftCount + counts.orderPendingCount + counts.orderFinalCount + counts.orderErrorCount || 0;
}

export function projectHasOffer(counts: ProjectStatisticsCounts): boolean {
  return getOfferTotal(counts) > 0;
}

export function projectHasOrder(counts: ProjectStatisticsCounts): boolean {
  return getOrderTotal(counts) > 0;
}

export function getQuoteColor(counts: ProjectStatisticsCounts): string {
  if (counts.quoteDraftCount > 0) return 'var(--apm-selection-quote)';
  if (counts.quoteExpiredCount > 0) return 'var(--apm-selection-expired)';

  return INACTIVE_ICON_COLOR;
}

export function getOfferColor(counts: ProjectStatisticsCounts): string {
  if (counts.offerDraftCount + counts.offerFinalCount > 0) return 'var(--apm-selection-offer)';
  if (counts.offerExpiredCount > 0) return 'var(--apm-selection-expired)';
  if (counts.offerLostCount > 0) return 'var(--apm-selection-lost)';
  if (counts.offerArchivedCount > 0) return 'var(--apm-selection-archived)';

  return INACTIVE_ICON_COLOR;
}

export function getOrderColor(counts: ProjectStatisticsCounts): string {
  if (counts.orderErrorCount > 0) return 'var(--apm-selection-error)';
  if (counts.orderDraftCount > 0) return 'var(--apm-selection-order-draft)';
  if (counts.orderPendingCount > 0) return 'var(--apm-selection-order-sending)';
  if (counts.orderFinalCount > 0) return 'var(--apm-selection-order)';

  return INACTIVE_ICON_COLOR;
}

export function getEnergyAnalysisColor(count: number): string {
  return count > 0 ? 'var(--apm-energy-analysis)' : INACTIVE_ICON_COLOR;
}

export function getQuoteTooltip(counts: ProjectStatisticsCounts, resourcesService: ResourcesService): string {
  const states = [
    { count: counts.quoteDraftCount, resourceId: STATUS_RESOURCE_IDS.quoteDraft },
    { count: counts.quoteExpiredCount, resourceId: STATUS_RESOURCE_IDS.quoteExpired },
  ];

  return buildTooltip(states, QUOTE_TOOLTIP_FALLBACK_RESOURCE_ID, resourcesService);
}

export function getOfferTooltip(counts: ProjectStatisticsCounts, resourcesService: ResourcesService): string {
  const states = [
    { count: counts.offerDraftCount, resourceId: STATUS_RESOURCE_IDS.offerDraft },
    { count: counts.offerFinalCount, resourceId: STATUS_RESOURCE_IDS.offerFinal },
    { count: counts.offerExpiredCount, resourceId: STATUS_RESOURCE_IDS.offerExpired },
    { count: counts.offerArchivedCount, resourceId: STATUS_RESOURCE_IDS.offerArchived },
    { count: counts.offerLostCount, resourceId: STATUS_RESOURCE_IDS.offerLost },
  ];

  return buildTooltip(states, OFFER_TOOLTIP_FALLBACK_RESOURCE_ID, resourcesService);
}

export function getOrderTooltip(counts: ProjectStatisticsCounts, resourcesService: ResourcesService): string {
  const states = [
    { count: counts.orderErrorCount, resourceId: STATUS_RESOURCE_IDS.orderError },
    { count: counts.orderDraftCount, resourceId: STATUS_RESOURCE_IDS.orderDraft },
    { count: counts.orderPendingCount, resourceId: STATUS_RESOURCE_IDS.orderPending },
    { count: counts.orderFinalCount, resourceId: STATUS_RESOURCE_IDS.orderFinal },
  ];

  return buildTooltip(states, ORDER_TOOLTIP_FALLBACK_RESOURCE_ID, resourcesService);
}
